import React, { useRef, useState } from 'react';
import PrivacyChoicesIcon from '../../assets/icons/privacy-choices.svg';
import FordOvalLogo from '../../assets/icons/ford-oval-logo.svg';
import LincolnStarLogo from '../../assets/icons/lincoln-star-logo.svg';
import LanguageChooserModal from './LanguageChooserModal';
import './GlobalFooter.css';

interface GlobalFooterProps {
  brand: 'Ford' | 'Lincoln';
  supportedLanguages?: string[];
  currentLocale?: string;
  onLanguageChange?: (languageCode: string) => void;
  showLanguageSelector?: boolean;
  /**
   * Opens the OneTrust cookie preference centre. When provided, a "Cookie
   * Settings" control renders between Privacy Notice and Your Privacy
   * Choices (this microsite's legal bar has always carried it; the FDS
   * skinny bar it was lifted from does not).
   */
  onCookieSettings?: () => void;
}

/**
 * The only locale codes this footer actually has translated copy/labels
 * for (WCAG 3.1.2). `currentLocale` reaches this component unfiltered —
 * an unsupported code must not be tagged `lang="en"` (or any other
 * language) since the text shown for it isn't reliably in that language.
 */
const KNOWN_LANGUAGE_CODES = ['en', 'es', 'fr'];

/**
 * Small "external link" glyph — FDS `GlobalFooter.jsx` draws this next to
 * the copyright link on the Lincoln skinny bar only (Ford's copyright link
 * carries no icon in the source component either).
 */
const ExternalLinkGlyph: React.FC = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
    <path d="M3.5 1.5H1.5v7h7v-2M6 1.5h2.5V4M8.5 1.5L4.5 5.5" fill="none" stroke="currentColor" strokeWidth="1.1" />
  </svg>
);

/**
 * ford.com's "Change Language" globe: the FDS `GfIcon kind="globe"` path
 * (GlobalFooter.jsx in the design-system export), drawn in Ford blue
 * (`--brand-deep`) the way ford.com's own footer draws it — the previous
 * black glyph was the design canvas's placeholder, not the production icon.
 */
const GlobeGlyph: React.FC = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" className="gf-lang-btn-icon">
    <path
      d="M14.7 21c1.7-2.5 2.6-5.3 2.8-8.2h3.9c-.3 3.9-3 7.1-6.7 8.2M2.6 12.8h3.9c.1 2.9 1.1 5.8 2.8 8.2-3.6-1.1-6.3-4.3-6.7-8.2M9.3 3c-1.7 2.5-2.6 5.3-2.8 8.2H2.6c.3-3.9 3-7.1 6.7-8.2m3.5 8.2V3c1.9 2.4 3 5.3 3.1 8.2zm0 1.6h3.1c-.2 2.9-1.2 5.8-3.1 8.2zm-1.6 0V21c-1.9-2.4-3-5.3-3.1-8.2zm0-1.6H8.1c.2-2.9 1.2-5.8 3.1-8.2zm10.2 0h-3.9c-.1-2.9-1.1-5.8-2.8-8.2 3.6 1.1 6.3 4.3 6.7 8.2M12 1C5.9 1 1 5.9 1 12s4.9 11 11 11 11-4.9 11-11S18.1 1 12 1"
      fill="var(--brand-deep, #00095b)"
    />
  </svg>
);

interface FooterItem {
  text: string;
  url?: string;
  onClick?: () => void;
  isPrivacyChoices?: boolean;
  isCopyright?: boolean;
}

/**
 * Web survey footer — the trimmed FDS `GlobalFooter` "skinny" bar from the
 * WEB variation of Screen 01 (`Screen 01 - About You (Web).dc.html`), not
 * the full ford.com footer. Lifted from the meridian-events-monorepo
 * web-app (SA-1681 fix-08) with two seams for this app: the OneTrust
 * "Cookie Settings" control and Spanish-domain links.
 *
 * `data-brand` on this component's OWN root (not inherited): the FDS
 * design tokens this file reads (`--surface-subtle`, `--text-strong`,
 * `--fill-interactive`, etc.) are defined on `[data-brand]` selectors in
 * `@latitude-digital/survey-core/styles`. This footer renders as a SIBLING
 * of the survey host, so it acts as its own token host.
 */
const GlobalFooter: React.FC<GlobalFooterProps> = ({
  brand,
  supportedLanguages = [],
  currentLocale = 'en',
  onLanguageChange = () => {},
  showLanguageSelector = false,
  onCookieSettings,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const langBtnRef = useRef<HTMLButtonElement>(null);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleLanguageSelect = (locale: string) => {
    onLanguageChange(locale);
    setIsModalOpen(false);
  };

  /**
   * The modal option's label text and the `lang` it should be tagged with
   * (WCAG 3.1.2) — these are NOT the same as the locale the button
   * represents: "Spanish" is the English WORD for the Spanish language
   * (lang="en"), while Lincoln's "Español" is the Spanish word for itself
   * (lang="es"). "English"/"French" are always the English exonym here, so
   * they always tag lang="en" too.
   */
  const getLanguageDisplayInfo = (locale: string): { text: string; lang?: string } => {
    if (locale === 'es') {
      return brand === 'Ford' ? { text: 'Spanish', lang: 'en' } : { text: 'Español', lang: 'es' };
    }
    if (locale === 'en') return { text: 'English', lang: 'en' };
    if (locale === 'fr') return { text: 'French', lang: 'en' };
    // Unknown locale: no display-name mapping exists, so the raw code is
    // shown verbatim — omit `lang` rather than mistag it.
    return { text: locale };
  };

  const filteredSupportedLanguages = supportedLanguages.filter(
    locale => locale !== 'Other' && locale !== 'other' && locale.length <= 5,
  );

  const year = new Date().getFullYear();
  const companyName = brand === 'Lincoln' ? 'Lincoln Motor Company' : 'Ford Motor Company';

  // Footer link + language-control translations
  const footerTranslations: Record<
    string,
    {
      copyright: string;
      privacyNotice: string;
      cookieSettings: string;
      privacyChoices: string;
      interestBasedAds: string;
      changeLanguage: string;
      chooseLanguage: string;
    }
  > = {
    en: {
      copyright: `© ${year} ${companyName}`,
      privacyNotice: 'Privacy Notice',
      cookieSettings: 'Cookie Settings',
      privacyChoices: 'Your Privacy Choices',
      interestBasedAds: 'Interest Based Ads',
      changeLanguage: 'Change Language',
      chooseLanguage: 'Please choose a language',
    },
    es: {
      copyright: `© ${year} ${companyName}`,
      privacyNotice: 'Aviso de Privacidad',
      cookieSettings: 'Configuraciones de Cookies',
      privacyChoices: 'Sus Opciones de Privacidad',
      interestBasedAds: 'Anuncios Basados en Intereses',
      changeLanguage: 'Cambiar idioma',
      chooseLanguage: 'Por favor elija un idioma',
    },
    fr: {
      copyright: `© ${year} ${companyName}`,
      privacyNotice: 'Avis de Confidentialité',
      cookieSettings: 'Paramètres des cookies',
      privacyChoices: 'Vos Choix de Confidentialité',
      interestBasedAds: 'Publicités Ciblées',
      changeLanguage: 'Changer de langue',
      chooseLanguage: 'Veuillez choisir une langue',
    },
  };

  const currentTranslations = footerTranslations[currentLocale] || footerTranslations['en'];

  // Localized domain URLs - Ford and Lincoln use subdomains for Spanish
  const fordDomain = currentLocale === 'es' ? 'https://es.ford.com' : 'https://www.ford.com';
  const lincolnDomain = currentLocale === 'es' ? 'https://es.lincoln.com' : 'https://www.lincoln.com';
  const brandDomain = brand === 'Lincoln' ? lincolnDomain : fordDomain;

  const cookieItem: FooterItem[] = onCookieSettings
    ? [{ text: currentTranslations.cookieSettings, onClick: onCookieSettings }]
    : [];

  // Brand-specific link mapping with localized URLs. `isCopyright` marks
  // the one link that carries the external-link glyph on Lincoln.
  const getFooterItems = (): FooterItem[] => {
    if (brand === 'Lincoln') {
      return [
        { text: currentTranslations.copyright, url: lincolnDomain, isCopyright: true },
        { text: currentTranslations.privacyNotice, url: `${lincolnDomain}/help/privacy-terms/` },
        ...cookieItem,
        {
          text: currentTranslations.privacyChoices,
          url: `${lincolnDomain}/help/privacy/ccpa/`,
          isPrivacyChoices: true,
        },
        { text: currentTranslations.interestBasedAds, url: `${lincolnDomain}/help/privacy-terms/#privacy` },
      ];
    }
    return [
      { text: currentTranslations.copyright, url: fordDomain, isCopyright: true },
      { text: currentTranslations.privacyNotice, url: `${fordDomain}/help/privacy/` },
      ...cookieItem,
      { text: currentTranslations.privacyChoices, url: `${fordDomain}/help/privacy/ccpa/`, isPrivacyChoices: true },
      { text: currentTranslations.interestBasedAds, url: `${fordDomain}/help/privacy/#USprivacynotice` },
    ];
  };

  const footerItems = getFooterItems();
  const shouldShowLanguageSelector = showLanguageSelector && filteredSupportedLanguages.length > 1;

  const renderItem = (item: FooterItem, index: number) => {
    const content = (
      <>
        {item.text}
        {item.isPrivacyChoices && (
          <img src={PrivacyChoicesIcon} alt="" aria-hidden="true" className="global-footer-privacy-icon" />
        )}
        {item.isCopyright && brand === 'Lincoln' && <ExternalLinkGlyph />}
      </>
    );
    if (item.onClick) {
      return (
        <button
          key={index}
          type="button"
          onClick={item.onClick}
          className="global-footer-link global-footer-link--button"
        >
          {content}
        </button>
      );
    }
    return (
      <a key={index} href={item.url} target="_blank" rel="noopener noreferrer" className="global-footer-link">
        {content}
      </a>
    );
  };

  return (
    <footer className={`global-footer-container brand-${brand.toLowerCase()}`} data-brand={brand.toLowerCase()}>
      {brand === 'Lincoln' ? (
        <>
          {shouldShowLanguageSelector && (
            <div className="gf-lincoln-utility">
              <button
                type="button"
                ref={langBtnRef}
                onClick={openModal}
                aria-haspopup="dialog"
                className="gf-lang-btn-lincoln"
              >
                {currentTranslations.changeLanguage.toUpperCase()}
              </button>
            </div>
          )}
          <div className="gf-lincoln-legal-bar">
            <div className="gf-lincoln-links">{footerItems.map(renderItem)}</div>
            <div className="gf-lincoln-logo">
              <a href={brandDomain} target="_blank" rel="noopener noreferrer" aria-label="Lincoln Home">
                <img src={LincolnStarLogo} alt="" aria-hidden="true" className="global-footer-logo-image" />
              </a>
            </div>
          </div>
        </>
      ) : (
        <div className="gf-skinny brand-ford">
          <div className="gf-lang-row">
            {shouldShowLanguageSelector && (
              <button type="button" ref={langBtnRef} onClick={openModal} aria-haspopup="dialog" className="gf-lang-btn">
                <GlobeGlyph />
                <span>{currentTranslations.changeLanguage}</span>
              </button>
            )}
          </div>
          <div className="gf-legal-links">{footerItems.map(renderItem)}</div>
          <div className="gf-logo">
            <a href={brandDomain} target="_blank" rel="noopener noreferrer" aria-label="Home">
              <img src={FordOvalLogo} alt="" aria-hidden="true" className="global-footer-logo-image" />
            </a>
          </div>
        </div>
      )}

      {shouldShowLanguageSelector && (
        <LanguageChooserModal
          isOpen={isModalOpen}
          brand={brand}
          languages={filteredSupportedLanguages}
          currentLocale={currentLocale}
          titleLang={KNOWN_LANGUAGE_CODES.includes(currentLocale) ? currentLocale : undefined}
          getLanguageLabel={getLanguageDisplayInfo}
          titleText={currentTranslations.chooseLanguage}
          onSelect={handleLanguageSelect}
          onClose={closeModal}
          triggerRef={langBtnRef}
        />
      )}
    </footer>
  );
};

export default GlobalFooter;
